/**
 * The public legal pages, written as Markdown in `apps/web/content/legal`.
 * Each file starts with `# Title` and a `Last updated:` line; the rest is the
 * body. The order here is the order of the page's navigation.
 */

const FILES = import.meta.glob<string>("/content/legal/*.md", {
	query: "?raw",
	import: "default",
	eager: true,
});

/** Navigation label per document; key order is the navigation order. */
const LABELS = {
	privacy: "Privacy",
	terms: "Terms",
	dpa: "DPA",
	subprocessors: "Subprocessors",
} as const;
type LegalSlug = keyof typeof LABELS;

/**
 * Not reviewed by counsel yet: the pages say so and ask search engines not to index them, so
 * no draft wording outlives the reviewed text in a search snippet. Flip once they are signed off.
 */
const DRAFT = true;

export interface LegalDocument {
	slug: LegalSlug;
	/** Short name for the navigation. */
	label: string;
	title: string;
	updated: string;
	/** First paragraph, for the meta description. */
	summary: string;
	body: string;
	/** Pending legal review: shown on the page, and kept out of search results. */
	draft: boolean;
}

function parse(slug: LegalSlug, label: string, source: string): LegalDocument {
	const [heading = "", ...rest] = source.trim().split("\n");
	const text = rest.join("\n").trim();
	const updated = text.match(/^Last updated: (.+)$/m)?.[1] ?? "";
	const body = text.replace(/^Last updated: .+\n+/, "");
	return {
		slug,
		label,
		title: heading.replace(/^#\s+/, ""),
		updated,
		summary:
			body.split("\n\n")[0]?.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") ??
			"",
		body,
		draft: DRAFT,
	};
}

export const LEGAL_DOCUMENTS: LegalDocument[] = Object.entries(LABELS).map(
	([slug, label]) =>
		parse(
			slug as LegalSlug,
			label,
			FILES[`/content/legal/${slug}.md`] ?? "",
		),
);

export function legalDocument(slug: string): LegalDocument | undefined {
	return LEGAL_DOCUMENTS.find((doc) => doc.slug === slug);
}
